import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

const propositions = [
  {
    number: '01',
    title: 'Economics and data science. Both, not one.',
    body: 'BSc Economics from Westminster. MSc Data Science at Loughborough. Most analysts can run models but lack the commercial frame. Most business graduates understand the logic but cannot measure it. The combination is uncommon, and the gap between those disciplines is where the most useful analytical work happens.',
    accent: '#9B3FFE',
  },
  {
    number: '02',
    title: 'Analytical rigour, not surface familiarity.',
    body: 'Dissertation-level econometric modelling. Churn prediction products delivered to paying clients. LLM evaluation for production systems. Regression modelling on real pay discrimination datasets with policy recommendations attached. These are not course exercises. They are the standard of work I hold myself to.',
    accent: '#00E5FF',
  },
  {
    number: '03',
    title: 'Every output is anchored to a decision.',
    body: 'Technical work without a clear so-what is just cost. Every analysis, model, and report I produce is framed around what someone needs to act on. The question is not what the data shows. It is what to change because of it. That framing comes from the economics training, not the data science.',
    accent: '#10B981',
  },
  {
    number: '04',
    title: 'Proven at volume, under real conditions.',
    body: 'Over 1,000 consulting engagements across four independently-vetted expert networks. Five-star client rating on Upwork. A 30% client acquisition improvement at Hokstad Consulting. AWS AI Scholar selection. Westminster Gold Employability Award. Bloomberg Market Concepts certified. The breadth is real and the track record is verifiable.',
    accent: '#F59E0B',
  },
];

const WhyMe = () => {
  const scrollToContact = () => {
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className="relative py-20 sm:py-28 md:py-40 overflow-hidden"
      data-testid="whyme-section"
    >
      <div className="absolute inset-0 bg-bg-surface/30" />
      <div className="absolute inset-0 dot-pattern opacity-30" />
      <div className="absolute top-0 left-0 right-0 h-px section-divider" />
      <div className="absolute bottom-0 left-0 right-0 h-px section-divider" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.75, ease: [0.25, 0.1, 0.25, 1] }}
          className="mb-16 md:mb-20"
        >
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-8 mb-6">
            <div>
              <div className="section-label mb-6">Differentiation</div>
              <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none font-medium">
                What Sets
                <br />
                <span className="gradient-text-brand">Me Apart</span>
              </h2>
            </div>
            <motion.button
              onClick={scrollToContact}
              className="group flex items-center gap-2 px-6 py-3.5 bg-white text-black rounded-full text-sm font-medium hover:bg-white/90 transition-all duration-300 self-start flex-shrink-0 mt-0 sm:mt-14"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
            >
              Start a conversation
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </motion.button>
          </div>

          {/* Edge line */}
          <p className="text-text-secondary text-lg md:text-xl max-w-2xl leading-relaxed">
            Most analysts describe the problem.{' '}
            <span className="text-white font-medium">
              I quantify it, structure it, and produce a recommendation with evidence behind it.
            </span>
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-5 md:gap-6">
          {propositions.map((prop, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.65, delay: i * 0.09, ease: [0.25, 0.1, 0.25, 1] }}
              className="group relative p-7 md:p-8 rounded-2xl border bg-bg-primary/70 hover:bg-bg-primary/90 transition-all duration-300 glow-border overflow-hidden"
              style={{ borderColor: 'rgba(255,255,255,0.08)' }}
            >
              {/* Accent glow */}
              <div
                className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                  background: `radial-gradient(circle, ${prop.accent}12 0%, transparent 70%)`,
                  transform: 'translate(30%, -30%)',
                }}
              />

              {/* Number + accent line */}
              <div className="flex items-center gap-3 mb-6">
                <span
                  className="text-[9px] font-medium tracking-[0.3em] uppercase"
                  style={{ color: prop.accent }}
                >
                  {prop.number}
                </span>
                <div
                  className="flex-1 h-px"
                  style={{ background: `linear-gradient(90deg, ${prop.accent}40, transparent)` }}
                />
              </div>

              <h3 className="font-display text-xl md:text-[1.35rem] font-medium tracking-tight mb-4 leading-tight">
                {prop.title}
              </h3>

              <p className="text-text-secondary text-sm leading-[1.78]">
                {prop.body}
              </p>

              <div
                className="absolute bottom-0 left-0 right-0 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: `linear-gradient(90deg, transparent, ${prop.accent}40, transparent)`,
                }}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyMe;
